import { Tooltip } from "@/components/atoms/Tooltip";
import { RAMP_LOAD } from "./sceneTheme";
import type { SlotProjection } from "./slotProjection";

interface Props {
  projection: SlotProjection;
}

/**
 * Where the slots go.
 *
 * The issue list says what is *wrong*; this says what each rule *costs*. Start
 * from every slot the window could theoretically hold, then subtract each rule
 * in the order the booking service applies it — so an operator can see that the
 * notice period is throwing away more than the weekend is.
 *
 * Losses are an ordered scale, so they take one hue light→dark; the final bar
 * is the only one wearing a status colour.
 */
export function SlotWaterfall({ projection }: Props) {
  const steps = projection.losses;
  const max = Math.max(1, ...steps.map((s) => s.running));

  return (
    <div className="h-full overflow-y-auto px-3 py-3">
      <ul className="space-y-1.5">
        {steps.map((step, i) => {
          const isLoss = step.removed > 0;
          const isFinal = step.id === "generated";
          // A loss bar spans from the new running total to the old one, so its
          // width is literally what the rule took away.
          const from = isLoss ? (step.running / max) * 100 : 0;
          const to = isLoss ? ((step.running + step.removed) / max) * 100 : (step.running / max) * 100;
          const fill = isFinal
            ? projection.active && step.running > 0
              ? "var(--bridge-ok, #1E7A38)"
              : "#7488A3"
            : RAMP_LOAD[Math.min(RAMP_LOAD.length - 1, i)];

          return (
            <li key={step.id} className="flex items-center gap-2">
              <span className="w-[124px] shrink-0 text-right text-[12.5px] leading-tight text-bridge-muted">
                {step.label}
              </span>
              <span className="relative h-4 flex-1">
                <Tooltip
                  side="top"
                  content={
                    isLoss
                      ? `${step.removed.toLocaleString()} slots removed`
                      : `${step.running.toLocaleString()} slots`
                  }
                >
                  <span
                    className="absolute top-0 block h-4 rounded-[3px]"
                    style={{
                      left: `${from}%`,
                      width: `${Math.max(1.5, to - from)}%`,
                      background: fill,
                    }}
                  />
                </Tooltip>
              </span>
              <span className="w-[60px] shrink-0 text-right font-mono text-[12.5px] font-semibold tabular-nums text-bridge-fg">
                {isLoss ? "−" : ""}
                {(isLoss ? step.removed : step.running).toLocaleString()}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="mt-3 border-t border-bridge-border pt-2 text-[11.5px] leading-relaxed text-bridge-subtle">
        Every slot the window could hold over {projection.horizonDays} days, minus each rule in the
        order the booking service applies it.
      </p>
    </div>
  );
}
